import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Layer, Stage } from 'react-konva';
import { Loader, PLATFORM_THEME_COLORS } from '@thinknet-gmbh/platform-common-ui';

import { useSetGanttEmployeesOrder } from 'src/hooks/gantts/set-gantt-employees-order.hook';
import { TimeStripeAddingModal } from 'src/pages/gantt/components/time-stripe-adding-modal';

import { GanttView } from './gantt-view';
import { useGetGanttData } from './gantt-calendar.hooks';
import { IGanttCalendar } from './gantt-calendar.interfaces';
import { HeaderLayer } from './gantt-view/components/header-layer';
import { HEADER_ROW_HEIGHT } from './gantt-view/gantt-view.constants';
import { GanttCalendarWrapper, EmployeeListWrapper } from './gantt-calendar.styles';
import { getGanttGridInfo, getNewEmployeeIdsOrder } from './gantt-view/gantt-view.helpers';
import { IActivePopoverInfo } from './gantt-view/gantt-view.interfaces';
import { GanttPopover } from './gantt-view/components/gantt-popover';
import { GANTT_VIEW_POPOVER_TYPES } from './gantt-view/components/gantt-popover/gantt-popover.constants';

const GanttCalendar: React.FC<IGanttCalendar> = ({
  employees,
  viewRange,
  ganttControl,
  absenceReasons,
  employeesWorkingHours,
  fontIsLoaded,
  containerWidth,
  colorSchema,
}) => {
  const { t } = useTranslation(['common', 'gantt', 'employee', 'projects']);
  const [dateBarLeftOffset, setDateBarLeftOffset] = useState(0);
  const [conflictModal, setConflictModal] = useState<any>();
  const [activePopoverInfo, setActivePopoverInfo] = useState<IActivePopoverInfo | null>();

  const setGanttEmployeesOrder = useSetGanttEmployeesOrder();
  const { loading, ganttData } = useGetGanttData(
    viewRange,
    employees,
    absenceReasons,
    employeesWorkingHours
  );
  const { fullWidth } = getGanttGridInfo(ganttData, containerWidth);

  const setPopoverInfo = (type: string) => (modalInfo: any) =>
    setActivePopoverInfo({ ...modalInfo, type });

  useEffect(() => setActivePopoverInfo(null), [ganttControl.fullScreenActive]);

  if (loading || !fontIsLoaded) return <Loader />;

  return (
    <>
      {activePopoverInfo?.type && (
        <GanttPopover
          t={t}
          ganttControl={ganttControl}
          popoverInfo={activePopoverInfo}
          onClose={() => setActivePopoverInfo(null)}
          setConflictModal={setConflictModal}
          setEmployeesOrder={(employeeId, newOrder) => {
            const ganttEmployeeIds = ganttData.map((data: any) => data.employee._id);
            const newEmployeeIdsOrder = getNewEmployeeIdsOrder({
              employeeId,
              newOrder,
              ganttEmployeeIds,
            });

            if (newEmployeeIdsOrder) {
              setGanttEmployeesOrder(newEmployeeIdsOrder);
            }
          }}
        />
      )}
      {conflictModal && (
        <TimeStripeAddingModal
          conflicts={conflictModal.conflicts}
          onSubmit={conflictModal.onConfirm}
          onCancel={() => setConflictModal(false)}
        />
      )}
      {!!ganttData.length && (
        <GanttCalendarWrapper>
          <EmployeeListWrapper>
            <Stage
              width={containerWidth}
              height={HEADER_ROW_HEIGHT}
              fill={PLATFORM_THEME_COLORS.primary.white}
            >
              <Layer x={-dateBarLeftOffset}>
                <HeaderLayer
                  t={t}
                  ganttData={ganttData}
                  fullWidth={fullWidth}
                  dateBarLeftOffset={dateBarLeftOffset}
                  onEmployeeClick={setPopoverInfo(GANTT_VIEW_POPOVER_TYPES.employeeInfo)}
                />
              </Layer>
            </Stage>
          </EmployeeListWrapper>

          <GanttView
            t={t}
            containerWidth={containerWidth}
            viewRange={viewRange}
            ganttData={ganttData}
            ganttControl={ganttControl}
            colorSchema={colorSchema}
            setDateBarLeftOffset={setDateBarLeftOffset}
            dateBarLeftOffset={dateBarLeftOffset}
            setActivePopoverInfo={setActivePopoverInfo}
            setConflictModal={setConflictModal}
          />
        </GanttCalendarWrapper>
      )}
    </>
  );
};

export default GanttCalendar;
